import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Download, CreditCard, Landmark, QrCode, CheckCircle2 } from 'lucide-react';

// Relative /api works on every public domain serving this page (app domain,
// preview, pollendots.com proxy). Raw localhost dev has no /api proxy, so fall
// back to the configured backend there.
const API_BASE = window.location.hostname === 'localhost' ? process.env.REACT_APP_BACKEND_URL : '';
const inr = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function BrandedDocPage() {
  const { customerSlug, docNumber } = useParams();
  const docKind = window.location.pathname.split('/')[2];
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const acceptQuote = async () => {
    if (!window.confirm(`Accept quote ${doc.number} for ₹${(doc.total || 0).toLocaleString('en-IN')}?`)) return;
    setAccepting(true);
    try {
      const r = await fetch(`${API_BASE}/api/public/docs/${doc.id}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setDoc((p) => ({ ...p, status: 'accepted', accepted_date: d.accepted_date, accepted_by: d.accepted_by, payment_link_url: d.payment_link_url || p.payment_link_url }));
    } catch (e) { alert(e.message); }
    setAccepting(false);
  };

  useEffect(() => {
    fetch(`${API_BASE}/api/public/docs/view/${customerSlug}/${docKind}/${docNumber}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setDoc)
      .catch(() => setErr(true));
  }, [customerSlug, docKind, docNumber]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCF8F7] text-[#581C2B] font-serif text-lg">Document not found</div>
  );
  if (!doc) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FCF8F7]"><Loader2 className="w-6 h-6 animate-spin text-[#581C2B]" /></div>
  );

  const title = { quote: 'Quote', invoice: 'Tax Invoice', receipt: 'Payment Receipt', credit_note: 'Credit Note', debit_note: 'Debit Note' }[doc.doc_type] || 'Document';
  const pdfUrl = `${API_BASE}/api/public/docs/${doc.id}.pdf`;
  const gstHalf = (doc.tax_total || 0) / 2;

  return (
    <div className="min-h-screen bg-[#FCF8F7] text-[#1A1A2E]" data-testid="branded-doc-page">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/monogram.png" alt="Pollen Dots" className="h-10 w-10" />
          <img src="/brand/wordmark.png" alt="POLLEN DOTS" className="h-5" />
        </div>

        <p className="text-xs tracking-[0.25em] text-[#B16E32] font-semibold uppercase">{title}</p>
        <h1 className="text-3xl sm:text-4xl font-serif text-[#581C2B] mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>{doc.number}</h1>
        <p className="text-sm text-gray-500 mt-1">{doc.date} · For <span className="font-semibold text-[#1A1A2E]">{doc.customer_name}</span>{doc.subject ? ` · ${doc.subject}` : ''}</p>
        {(doc.against_number || doc.reason) && (
          <p className="text-xs text-gray-500 mt-1" data-testid="doc-against-line">
            {doc.against_number ? `Against invoice ${doc.against_number}` : ''}{doc.against_number && doc.reason ? ' · ' : ''}{doc.reason || ''}
          </p>
        )}
        {doc.status === 'cancelled' && <p className="mt-2 inline-block px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">CANCELLED</p>}
        {doc.status === 'accepted' && (
          <p className="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold" data-testid="quote-accepted-badge">
            <CheckCircle2 className="w-3.5 h-3.5" />ACCEPTED{doc.accepted_date ? ` · ${doc.accepted_date}` : ''}{doc.accepted_by ? ` · ${doc.accepted_by}` : ''}
          </p>
        )}

        <div className="mt-6 bg-white rounded-2xl border border-[#EBD9CD] p-6 shadow-sm">
          {(doc.lines || (doc.line ? [doc.line] : [])).map((ln, i, arr) => (
            <div key={i} className={i < arr.length - 1 ? 'pb-4 mb-4 border-b border-dashed border-[#EBD9CD]' : ''}>
              {ln.title && <p className="font-semibold">{ln.title}{doc.doc_type !== 'receipt' && ln.rate > 0 ? <span className="float-right text-[#581C2B]">{inr(ln.qty * ln.rate)}</span> : null}</p>}
              {doc.doc_type !== 'receipt' && ln.rate > 0 && <p className="text-xs text-gray-400">{ln.qty} × {inr(ln.rate)}</p>}
              {ln.description && <p className="text-sm text-gray-600 whitespace-pre-line mt-2">{ln.description}</p>}
            </div>
          ))}
          <div className="mt-4 pt-4 border-t border-dashed border-[#EBD9CD] space-y-1.5 text-sm">
            {doc.doc_type !== 'receipt' && (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Sub Total</span><span>{inr(doc.subtotal)}</span></div>
                {doc.tax_mode === 'cgst_sgst' ? (
                  <>
                    <div className="flex justify-between"><span className="text-gray-500">CGST (2.5%)</span><span>{inr(gstHalf)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500">SGST (2.5%)</span><span>{inr(gstHalf)}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between"><span className="text-gray-500">IGST ({doc.gst_rate}%)</span><span>{inr(doc.tax_total)}</span></div>
                )}
              </>
            )}
            <div className="flex justify-between text-base font-bold text-[#581C2B] pt-1" data-testid="branded-doc-total"><span>Total</span><span>{inr(doc.total)}</span></div>
            {doc.doc_type === 'invoice' && (
              <>
                {doc.payments_made > 0 && <div className="flex justify-between"><span className="text-gray-500">Payments Made</span><span>(-) {inr(doc.payments_made)}</span></div>}
                <div className="flex justify-between font-semibold"><span>Balance Due</span><span>{inr(doc.balance_due)}</span></div>
              </>
            )}
            {doc.doc_type === 'quote' && doc.payments_made > 0 && (
              <>
                <div className="flex justify-between text-emerald-700"><span>Paid</span><span>(-) {inr(doc.payments_made)}</span></div>
                <div className="flex justify-between font-semibold"><span>Balance</span><span data-testid="quote-balance">{inr(Math.max((doc.total || 0) - doc.payments_made, 0))}</span></div>
              </>
            )}
            {doc.doc_type === 'receipt' && doc.payment_mode && <div className="flex justify-between"><span className="text-gray-500">Mode</span><span>{doc.payment_mode}</span></div>}
          </div>
          {doc.notes && <p className="mt-4 pt-3 border-t border-dashed border-[#EBD9CD] text-sm italic text-[#581C2B]">{doc.notes}</p>}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          {doc.doc_type === 'quote' && doc.status === 'issued' && (
            <button onClick={acceptQuote} disabled={accepting} data-testid="accept-quote-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-700 text-white px-6 py-3.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Accept Quote
            </button>
          )}
          <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="branded-download-pdf"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#581C2B] text-white px-6 py-3.5 text-sm font-semibold hover:opacity-90 transition-opacity">
            <Download className="w-4 h-4" />Download PDF
          </a>
          {doc.payment_link_url && doc.status !== 'cancelled' && (
            <a href={doc.payment_link_url} target="_blank" rel="noreferrer" data-testid="branded-pay-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#B16E32] text-white px-6 py-3.5 text-sm font-semibold hover:opacity-90 transition-opacity">
              <CreditCard className="w-4 h-4" />{doc.doc_type === 'quote' && doc.status === 'accepted' ? 'Pay Now — Cards · UPI · Netbanking' : 'Pay Securely (Razorpay)'}
            </a>
          )}
        </div>

        {['quote', 'invoice'].includes(doc.doc_type) && (
          <div className="mt-6 bg-white rounded-2xl border border-[#EBD9CD] p-6 shadow-sm">
            <p className="text-xs tracking-[0.2em] text-[#B16E32] font-semibold uppercase mb-4 flex items-center gap-2"><Landmark className="w-3.5 h-3.5" />Payment Options</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-4 text-sm">
                {(doc.banks || []).map((b) => (
                  <div key={b.bank}>
                    <p className="font-semibold text-[#581C2B]">{b.bank}</p>
                    <p className="text-gray-600">{b.name}</p>
                    <p className="text-gray-600">A/c: <span className="font-mono">{b.account}</span></p>
                    <p className="text-gray-600">IFSC: <span className="font-mono">{b.ifsc}</span></p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col items-center justify-center text-center">
                <img src="/brand/idfc_qr.png" alt="UPI QR" className="w-36 h-36 rounded-lg border border-[#EBD9CD]" data-testid="branded-upi-qr" />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><QrCode className="w-3 h-3" />Scan with any UPI app</p>
                <p className="text-sm font-semibold text-[#581C2B]">{doc.upi_id}</p>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-[11px] tracking-[0.3em] text-[#B16E32] mt-10 uppercase">Travel · Connect · Care — pollendots.com</p>
      </div>
    </div>
  );
}
